import React, { useMemo, useState, useEffect } from 'react'
import { Modal, Stack } from '~/components/_layouts'
import { UserIcon } from '@heroicons/react/24/outline'
import { useCandidate } from './CandidateProvider'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../lib/ui/select'
import { Button } from '../../lib/ui/button'
import { cn } from '~/lib/utils'

const candidateStatusOptions = {
  active: 'Active Search',
  paused: 'Paused',
  landed: 'Landed',
  paused_expired: 'Paused & Expired',
}

const CandidateStatusDialog = () => {
  const {
    showCandidateStatusDialog,
    toggleShowCandidateStatusDialog,
    candidateStatus,
    candidateStates,
    setNewCandidateStatus,
    candidate,
  } = useCandidate()

  // Map initial status label -> key ("active" | "paused" | "landed")
  const initialKey = useMemo(
    () =>
      Object.entries(candidateStatusOptions).find(
        ([, label]) => label === candidateStatus,
      )?.[0] || '',
    [candidateStatus],
  )

  const [statusKey, setStatusKey] = useState(initialKey)

  // --- Helpers --------------------------------------------------------

  // Format Date → local YYYY-MM-DD (for <input type="date">)
  const formatLocalDate = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Convert YYYY-MM-DD → local human-readable text for copy/UI
  const friendlyFromISO = (yyyyMmDd) => {
    if (!yyyyMmDd) return ''
    const [y, m, d] = yyyyMmDd.split('-').map(Number)
    const local = new Date(y, m - 1, d) // local midnight, no UTC shift
    return local.toLocaleDateString()
  }

  // Convert YYYY-MM-DD → backend format MM/DD/YYYY
  const backendDateFormat = (yyyyMmDd) => {
    if (!yyyyMmDd) return ''
    const [y, m, d] = yyyyMmDd.split('-')
    return `${m}/${d}/${y}`
  }

  // Parse YYYY-MM-DD as a LOCAL Date object (avoid UTC)
  const parseLocalISO = (yyyyMmDd) => {
    if (!yyyyMmDd) return null
    const [y, m, d] = yyyyMmDd.split('-').map(Number)
    return new Date(y, m - 1, d)
  }

  // Normalize to start of local day
  const startOfDay = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate())

  // Rails parity: end_date.to_date.past? -> true iff end_date < today
  const isPastLocalISO = (yyyyMmDd) => {
    const end = parseLocalISO(yyyyMmDd)
    if (!end) return false
    const endStart = startOfDay(end)
    const todayStart = startOfDay(new Date())
    return endStart.getTime() < todayStart.getTime()
  }

  // -------------------------------------------------------------------

  // Default pause end date = today + 30 days, local time
  const defaultPauseEndDate = useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() + 30)
    return formatLocalDate(d)
  }, [])

  const [pauseEndDate, setPauseEndDate] = useState(defaultPauseEndDate)

  // If user switches to "paused" and we have no pauseEndDate, seed with default
  useEffect(() => {
    if (statusKey === 'paused' && !pauseEndDate) {
      setPauseEndDate(defaultPauseEndDate)
    }
  }, [statusKey, pauseEndDate, defaultPauseEndDate])

  // If the dialog opens later while candidateStatus changed externally
  useEffect(() => {
    setStatusKey(initialKey)
  }, [initialKey, showCandidateStatusDialog])

  const isUS = candidate?.isUS === true

  const handleSave = () => {
    // Format date for backend if needed
    const formattedDate =
      statusKey === 'paused' && pauseEndDate
        ? backendDateFormat(pauseEndDate)
        : null

    const payload =
      statusKey === 'paused' && isUS
        ? { status: statusKey, pause: { end_date: formattedDate } }
        : { status: statusKey }

    setNewCandidateStatus(payload)
  }

  // -------------------------------------------------------------------

  return (
    <Modal
      title={
        <Stack direction="row" spacing={2}>
          <UserIcon className="h-6 w-6" />
          Candidate Program Status
        </Stack>
      }
      size="md"
      open={showCandidateStatusDialog}
      onClose={() => toggleShowCandidateStatusDialog()}
    >
      {initialKey === 'paused_expired' && (
        <div
          id="unpause-expired-info"
          className="alert alert-warning mb-4 text-center"
        >
          Unpausing this candidate will extend their expiration date by the
          paused duration.
        </div>
      )}
      <div className="text-center">
        Candidates are in an active state unless they have a need to pause their
        program or have landed in a new role. You can update candidate state
        using the dropdown below. If a candidate is moved into a landed state,
        you will not be able to move them back into an active search state.
      </div>

      <div className="mt-4 flex justify-center">
        <Select
          onValueChange={(v) => setStatusKey(v)}
          defaultValue={initialKey}
          value={statusKey}
        >
          <SelectTrigger className="sm:w-4/6">
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(candidateStatusOptions).map(([key, value]) => (
              <SelectItem key={key} value={key}>
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* candidate-pausing (US only), shown only when paused */}
      {initialKey !== 'paused' && isUS && statusKey === 'paused' && (
        <div
          id="candidate-pausing"
          className="mt-6 rounded-2xl border p-4 sm:p-6"
        >
          <p className="mb-4 text-sm">
            Please select an end pause date for{' '}
            <span className="font-medium">
              {candidate?.firstName || 'the candidate'}
            </span>
            . If the end pause date is not currently known, the system will
            default to an end pause date 30 days from today, on{' '}
            <span className="font-medium">
              {friendlyFromISO(defaultPauseEndDate)}
            </span>
            . Please ensure the candidate has been advised of the date their
            program will resume.
          </p>

          <div className="mx-auto grid max-w-2xl grid-cols-1 gap-2">
            <label
              htmlFor="pause_end_date"
              className="text-sm font-medium text-gray-700"
            >
              Pause End Date
            </label>
            <input
              id="pause_end_date"
              type="date"
              className="rounded-xl border px-3 py-2"
              value={pauseEndDate}
              onChange={(e) => setPauseEndDate(e.target.value)}
            />
            <p className="text-xs text-gray-500">
              Defaults to {friendlyFromISO(defaultPauseEndDate)}.
            </p>
          </div>
        </div>
      )}

      <div className="mt-6 flex justify-center">
        <Button onClick={handleSave} disabled={!statusKey}>
          Save
        </Button>
      </div>

      {/* State History Table */}
      {candidateStates.length > 0 && (
        <div className="mt-10">
          <h3 className="mb-4 text-center text-lg font-semibold">
            State History
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 text-center font-medium">State</th>
                  <th className="p-2 text-center font-medium">Time</th>
                  <th className="p-2 text-center font-medium">
                    Scheduled End of Pause
                  </th>
                  <th className="p-2 text-center font-medium">Current</th>
                </tr>
              </thead>
              <tbody>
                {candidateStates.map((state, i) => {
                  // robust, local-time comparison (Rails: end_date.to_date.past?)
                  const pauseEnded = state.end_date
                    ? isPastLocalISO(state.end_date)
                    : false

                  return (
                    <tr
                      key={i}
                      className={cn(
                        'border-t',
                        state.current ? 'bg-gray-50 font-medium' : '',
                      )}
                    >
                      <td className="p-2 text-center capitalize">
                        {state.state?.replace('_', ' ')}
                      </td>
                      <td className="p-2 text-center">{state.time}</td>
                      <td className="p-2 text-center">
                        {state.end_date
                          ? pauseEnded
                            ? 'Pause Ended'
                            : friendlyFromISO(state.end_date)
                          : ''}
                      </td>
                      <td className="p-2 text-center">
                        {state.current ? 'Yes' : 'No'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default CandidateStatusDialog
